/**
 * メンバータブ。
 * メンバーの追加・削除に加えて、スプリの差別化機能である
 * 「精算の制約」をここで設定する。
 */
import {
  findMyMember,
  memberRemovability,
  useAddMember,
  useClaimMember,
  useDeleteConstraint,
  useFeatures,
  useReleaseMember,
  useRemoveMember,
  useSpli,
  useUpsertConstraint,
} from "@spli/app-logic";
import {
  CONSTRAINT_LABELS,
  constraintKindSchema,
  type ConstraintKind,
  type GroupDto,
} from "@spli/core";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { LoginUpsell } from "../../components/LoginUpsell.js";
import { EmptyState, ErrorView } from "../../components/StatusViews.js";

const CONSTRAINT_HINTS: Record<ConstraintKind, string> = {
  prefer: "送金の回数が同じなら、この2人の間で送金するようにします",
  avoid: "この2人の間では送金せず、別の人を経由します",
  cover: "2人目の負担がなくなり、その分を1人目が支払います",
};

/** 2人を選ぶ欄の見出し。cover だけは向きに意味がある */
const MEMBER_LABELS: Record<ConstraintKind, [string, string]> = {
  prefer: ["1人目", "2人目"],
  avoid: ["1人目", "2人目"],
  cover: ["負担する人", "負担してもらう人"],
};

export function MembersView({ group }: { group: GroupDto }) {
  const { user } = useSpli();
  const features = useFeatures();
  const myMember = findMyMember(group.members, user?.id ?? null);
  const claimMember = useClaimMember(group.shareToken);
  const releaseMember = useReleaseMember(group.shareToken);
  const addMember = useAddMember(group.shareToken);
  const removeMember = useRemoveMember(group.shareToken);
  const upsertConstraint = useUpsertConstraint(group.shareToken);
  const deleteConstraint = useDeleteConstraint(group.shareToken);

  const [newName, setNewName] = useState("");
  /** 削除できないときの理由 (押したときだけ出す) */
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [memberIdA, setMemberIdA] = useState("");
  const [memberIdB, setMemberIdB] = useState("");
  const [kind, setKind] = useState<ConstraintKind>("prefer");

  const nameOf = (id: string) => group.members.find((m) => m.id === id)?.name ?? "(削除済み)";

  /*
   * 「送金を避ける」は第三者を経由して精算する仕組みなので、
   * 3 人未満では経由できる相手が居らず、指定しても結果が変わらない。
   * 選べないようにしておく。
   */
  const availableKinds = constraintKindSchema.options.filter(
    (option) => option !== "avoid" || group.members.length >= 3,
  );

  const [labelA, labelB] = MEMBER_LABELS[kind];

  /**
   * メンバーの削除。
   *
   * 支出や精算に登場しているメンバーを消すと履歴の整合性が壊れるため、
   * サーバーは 400 を返す。同じ判定を手元でも行い、押した時点で理由を出す。
   * 理由を一覧に常時表示すると行が読みにくくなるので、押すまでは出さない。
   */
  const handleRemoveMember = (member: GroupDto["members"][number]): void => {
    const removability = memberRemovability(group, member.id);
    if (!removability.canRemove) {
      const howTo =
        removability.expenseCount > 0
          ? "支出の割り勘から外すか、支出を削除すると消せます。"
          : "精算の記録を取り消すと消せます。";
      setRemoveError(`${member.name} は${removability.reason}。${howTo}`);
      return;
    }
    setRemoveError(null);
    if (window.confirm(`${member.name} を削除しますか?`)) {
      removeMember.mutate(member.id);
    }
  };

  const handleAddMember = (event: FormEvent): void => {
    event.preventDefault();
    if (newName.trim() === "") return;
    addMember.mutate({ name: newName.trim() }, { onSuccess: () => setNewName("") });
  };

  const handleAddConstraint = (event: FormEvent): void => {
    event.preventDefault();
    if (!memberIdA || !memberIdB || memberIdA === memberIdB) return;
    upsertConstraint.mutate({ memberIdA, memberIdB, kind });
  };

  /** 制約 1 件を「けん → ゆい の分を負担」のような文にする */
  const describe = (c: GroupDto["constraints"][number]): string => {
    const a = nameOf(c.memberIdA);
    const b = nameOf(c.memberIdB);
    if (c.kind === "cover") return `${a} が ${b} の金額を負担する`;
    if (c.kind === "avoid") return `${a} と ${b} の間の送金を避ける`;
    return `${a} と ${b} の間の送金を優先する`;
  };

  return (
    <>
      {/* --- 自分との紐づけ (ログイン時のみ) --- */}
      {user ? (
        <section className="card">
          <div className="card__title">
            <span>このグループでの自分</span>
            {myMember && <span className="badge">{myMember.name}</span>}
          </div>
          <p className="card__hint">
            自分を選んで紐づけると、
            {features.payoutAccounts ? "自分への送金に受取先が表示され、" : ""}
            自分宛の送金に「受け取りました」の記録をつけられます。
          </p>

          <div className="chip-row">
            {group.members.map((member) => {
              const takenByOther = Boolean(member.userId) && member.userId !== user.id;
              const isMe = member.id === myMember?.id;
              return (
                <button
                  key={member.id}
                  type="button"
                  className={`chip${isMe ? " chip--selected" : ""}`}
                  aria-pressed={isMe}
                  disabled={takenByOther || claimMember.isPending || releaseMember.isPending}
                  title={takenByOther ? "別のアカウントと紐づいています" : undefined}
                  onClick={() =>
                    isMe ? releaseMember.mutate(member.id) : claimMember.mutate(member.id)
                  }
                >
                  {member.name}
                  {takenByOther ? " (他の人)" : ""}
                </button>
              );
            })}
          </div>

          {claimMember.isError && <ErrorView error={claimMember.error} />}

          <p className="muted" style={{ marginTop: 12 }}>
            {myMember ? (
              features.payoutAccounts ? (
                <>
                  受取先は <Link to="/account">アカウント設定</Link>{" "}
                  で登録できます。もう一度押すと紐づけを解除します。
                </>
              ) : (
                "もう一度押すと紐づけを解除します。"
              )
            ) : (
              "自分の名前をタップすると紐づけられます。"
            )}
          </p>
        </section>
      ) : (
        <LoginUpsell
          icon="raise-hand"
          title="ログインすると「自分」を紐づけられます"
          description={
            features.payoutAccounts
              ? "自分への送金に受取口座を表示したり、受け取り完了を記録したりできるようになります。"
              : "自分宛の送金に「受け取りました」の記録をつけられるようになります。"
          }
        />
      )}

      <section className="card">
        <h2 className="card__title">メンバー</h2>
        {group.members.length === 0 ? (
          <EmptyState icon="members">メンバーを追加してください。</EmptyState>
        ) : (
          <div className="list">
            {group.members.map((member) => (
              <div key={member.id} className="list-item">
                <span className="list-item__body">
                  <span className="list-item__title">{member.name}</span>
                </span>
                <button
                  type="button"
                  className="button button--ghost button--small"
                  aria-label={`${member.name} を削除`}
                  disabled={removeMember.isPending}
                  onClick={() => handleRemoveMember(member)}
                >
                  削除
                </button>
              </div>
            ))}
          </div>
        )}

        {/* 削除できない理由は、押したときにここへ出す (一覧を読みやすく保つため) */}
        {removeError && (
          <p className="notice notice--warning" style={{ marginTop: 12 }} role="alert">
            {removeError}
          </p>
        )}
        {/* 他の端末で支出が追加された直後など、事前判定をすり抜けた場合の受け皿 */}
        {removeMember.isError && <ErrorView error={removeMember.error} />}

        <form onSubmit={handleAddMember} className="row" style={{ marginTop: 12 }}>
          <input
            className="input grow"
            aria-label="追加するメンバーの名前"
            value={newName}
            placeholder="名前を入力"
            onChange={(event) => setNewName(event.target.value)}
          />
          <button type="submit" className="button" disabled={addMember.isPending}>
            追加
          </button>
        </form>
        {addMember.isError && <ErrorView error={addMember.error} />}
      </section>

      {/*
        制約は「2人の間」の指定なので、相手が居ない (メンバーが 2 人未満の)
        状態では何も変わらない。訊かない。
      */}
      {group.members.length < 2 ? null : (
      <section className="card">
        <h2 className="card__title">精算の制約</h2>
        <p className="card__hint">
          「誰が誰に払うか」「誰が誰の分を負担するか」を指定できます。
          結果は「精算」タブで確認できます。
        </p>

        {group.constraints.length > 0 && (
          <div className="list" style={{ marginBottom: 12 }}>
            {group.constraints.map((constraint) => (
              <div key={constraint.id} className="list-item">
                <span className="list-item__body">
                  <span className="list-item__title">{describe(constraint)}</span>
                </span>
                <button
                  type="button"
                  className="button button--ghost button--small"
                  aria-label={`${describe(constraint)} を削除`}
                  onClick={() => deleteConstraint.mutate(constraint.id)}
                >
                  削除
                </button>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={handleAddConstraint}>
          {/* 種類を先に選ぶ。cover かどうかで 2 人の意味が変わるため */}
          <div className="field">
            <label className="field__label" htmlFor="constraint-kind">
              制約の種類
            </label>
            <select
              id="constraint-kind"
              className="select"
              value={kind}
              onChange={(event) => setKind(event.target.value as ConstraintKind)}
            >
              {availableKinds.map((option) => (
                <option key={option} value={option}>
                  {CONSTRAINT_LABELS[option]}
                </option>
              ))}
            </select>
            <p className="muted" style={{ marginTop: 4 }}>
              {CONSTRAINT_HINTS[kind]}
            </p>
          </div>

          <div className="row row--wrap">
            <div className="field grow" style={{ marginBottom: 0 }}>
              <label className="field__label" htmlFor="constraint-member-a">
                {labelA}
              </label>
              <select
                id="constraint-member-a"
                className="select"
                value={memberIdA}
                onChange={(event) => setMemberIdA(event.target.value)}
              >
                <option value="">選択してください</option>
                {group.members.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field grow" style={{ marginBottom: 0 }}>
              <label className="field__label" htmlFor="constraint-member-b">
                {labelB}
              </label>
              <select
                id="constraint-member-b"
                className="select"
                value={memberIdB}
                onChange={(event) => setMemberIdB(event.target.value)}
              >
                <option value="">選択してください</option>
                {group.members.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            type="submit"
            className="button button--block"
            style={{ marginTop: 12 }}
            disabled={upsertConstraint.isPending || !memberIdA || !memberIdB || memberIdA === memberIdB}
          >
            制約を追加
          </button>
        </form>
        {upsertConstraint.isError && <ErrorView error={upsertConstraint.error} />}
      </section>
      )}
    </>
  );
}
